/**
 * Global Loader Store - Alpine.js
 *
 * Centralized loading state management with multiple concurrent loaders.
 * Uses plain object for Alpine.js reactivity (Map is not reactive).
 *
 * Usage:
 * - Alpine.store('loader').show('operation-id', 'Loading message')
 * - Alpine.store('loader').hide('operation-id')
 * - window.Loader.show() / hide() for non-Alpine contexts
 * - $store.loader.isActive('id') / hasAny() in templates
 */

export default {
  // Plain object for Alpine reactivity (Map is NOT reactive)
  activeLoaders: {},

  // Counter to track changes (for reactivity)
  _changeCounter: 0,

  // Timeout IDs stored separately (not reactive needed)
  _timeouts: {},

  // Configuration
  config: {
    defaultMessage: 'Loading...',
    defaultTimeout: 30000, // 30 seconds auto-cleanup
    enableAutoCleanup: true,
  },

  /**
   * Initialize the loader store
   * Called automatically by Alpine during alpine:init
   */
  init() {
    var ds = document.documentElement && document.documentElement.dataset;
    if (ds && ds.loadingDefault) {
      this.config.defaultMessage = ds.loadingDefault;
    }

    // Expose global API for non-Alpine contexts
    window.Loader = {
      show: (id, message, timeout) => this.show(id, message, timeout),
      hide: id => this.hide(id),
      hideAll: () => this.hideAll(),
      isActive: id => this.isActive(id),
      hasAny: () => this.hasAny(),
      getMessage: id => this.getMessage(id),
      getFirstMessage: () => this.getFirstMessage(),
    };

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.hideAll();
    });
  },

  /**
   * Show a loader with unique ID
   *
   * @param {string} id - Unique loader identifier (e.g., 'cart-update', 'wishlist-add')
   * @param {string} [message] - Optional custom message to display
   * @param {number} [timeout] - Optional custom timeout in ms (default: 30000)
   * @returns {void}
   */
  show(id, message = null, timeout = null) {
    if (!id) {
      return;
    }

    // Clear existing timeout if loader already exists
    if (this._timeouts[id]) {
      clearTimeout(this._timeouts[id]);
      delete this._timeouts[id];
    }

    // Set up auto-cleanup timeout
    if (this.config.enableAutoCleanup) {
      const timeoutDuration = timeout || this.config.defaultTimeout;
      this._timeouts[id] = setTimeout(() => {
        this.hide(id);
      }, timeoutDuration);
    }

    // Add or update loader (triggers Alpine reactivity)
    this.activeLoaders[id] = {
      message: message || this.config.defaultMessage,
      timestamp: Date.now(),
    };

    // Increment counter to ensure reactivity triggers
    this._changeCounter++;

    // Update DOM for accessibility
    this.updateAriaLive();
  },

  /**
   * Hide a specific loader by ID
   *
   * @param {string} id - Loader identifier to hide
   * @returns {boolean} - True if loader was active and hidden
   */
  hide(id) {
    if (!id) {
      return false;
    }

    if (!this.activeLoaders[id]) {
      return false;
    }

    // Clear timeout if exists
    if (this._timeouts[id]) {
      clearTimeout(this._timeouts[id]);
      delete this._timeouts[id];
    }

    // Remove loader (triggers Alpine reactivity)
    delete this.activeLoaders[id];

    // Increment counter to ensure reactivity triggers
    this._changeCounter++;

    // Update DOM for accessibility
    this.updateAriaLive();

    return true;
  },

  /**
   * Hide all active loaders
   * Useful for error states or page navigation
   *
   * @returns {number} - Number of loaders hidden
   */
  hideAll() {
    // Clear all timeouts
    Object.keys(this._timeouts).forEach(id => {
      clearTimeout(this._timeouts[id]);
    });
    this._timeouts = {};

    const count = Object.keys(this.activeLoaders).length;

    // Clear all loaders
    this.activeLoaders = {};

    // Increment counter to ensure reactivity triggers
    this._changeCounter++;

    // Update DOM for accessibility
    this.updateAriaLive();

    return count;
  },

  /**
   * Check if a specific loader is active
   *
   * @param {string} id - Loader identifier
   * @returns {boolean} - True if loader is active
   */
  isActive(id) {
    return !!this.activeLoaders[id];
  },

  /**
   * Check if any loader is active
   *
   * @returns {boolean} - True if any loader is active
   */
  hasAny() {
    return Object.keys(this.activeLoaders).length > 0;
  },

  /**
   * Get the count of active loaders
   *
   * @returns {number} - Number of active loaders
   */
  getCount() {
    return Object.keys(this.activeLoaders).length;
  },

  /**
   * Get message for a specific loader
   *
   * @param {string} id - Loader identifier
   * @returns {string|null} - Loader message or null
   */
  getMessage(id) {
    const loader = this.activeLoaders[id];
    return loader ? loader.message : null;
  },

  /**
   * Get the first active loader message
   * Useful for displaying a single message when multiple loaders are active
   *
   * @returns {string} - First loader message or default message
   */
  getFirstMessage() {
    const keys = Object.keys(this.activeLoaders);
    if (keys.length === 0) {
      return this.config.defaultMessage;
    }

    const firstLoader = this.activeLoaders[keys[0]];
    return firstLoader ? firstLoader.message : this.config.defaultMessage;
  },

  /**
   * Get all active loader IDs
   *
   * @returns {string[]} - Array of active loader IDs
   */
  getActiveIds() {
    return Object.keys(this.activeLoaders);
  },

  /**
   * Update ARIA live region for accessibility
   * Screen readers will announce loading state changes
   *
   * @private
   */
  updateAriaLive() {
    // Dispatch custom event for ARIA live region updates
    window.dispatchEvent(
      new CustomEvent('loader-state-changed', {
        detail: {
          hasAny: this.hasAny(),
          count: this.getCount(),
          message: this.getFirstMessage(),
        },
      })
    );
  },

  /**
   * Update configuration
   *
   * @param {Object} config - Configuration overrides
   * @returns {void}
   */
  updateConfig(config) {
    this.config = { ...this.config, ...config };
  },

  /**
   * Get loader statistics (for debugging)
   *
   * @returns {Object} - Statistics object
   */
  getStats() {
    const loaders = Object.entries(this.activeLoaders).map(([id, data]) => ({
      id,
      message: data.message,
      duration: Date.now() - data.timestamp,
    }));

    return {
      activeCount: Object.keys(this.activeLoaders).length,
      loaders: loaders,
      config: this.config,
    };
  },
};
